// @ts-strict-ignore
import * as React from 'react';
import {clsx} from 'clsx';

import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {SvgIconType} from 'common/components/svg-icon/types';
import {TooltipAttrs} from 'common/components/tooltip/types';

import {Entry, RowLabelTheme} from '../types';

export function RowLabel(props: {
  entry: Entry;
  hasNewUI: boolean;
  index: number;
  isPlaceholder: boolean;
  isReferencedMetrics: boolean;
  onGroupClick: () => void;
  onClick: () => void;
  reportName: string;
  rowIcon: SvgIconType;
  rowIndexToggleable: boolean;
  rowLabel: string;
  rowLabelPrefix: string;
  rowLabelTheme: RowLabelTheme;
  rowLabelPrefixIcon: SvgIconType;
}) {
  const isClickable = !props.isPlaceholder && props.rowIndexToggleable;
  const extraClass = {
    ...(props.reportName && {[`for-${props.reportName}`]: true, 'is-hidden': props.entry.isHidden}),
    'is-referenced-metrics': props.isReferencedMetrics,
    'is-gray': props.rowLabelTheme === RowLabelTheme.Gray,
    'is-session-replay-filter': props.rowLabelTheme === RowLabelTheme.SessionReplayLabel,
  };
  const isDisabled = props.rowLabelTheme === RowLabelTheme.SessionReplayLabel;

  return (
    <>
      {/** optional prefixed grouped label */}
      {!!props.rowLabelPrefix && (!props.hasNewUI ? props.index === 0 : true) && (
        <RowLabelContent
          icon={props.rowLabelPrefixIcon}
          label={props.rowLabelPrefix}
          onClick={isClickable && props.onGroupClick}
          extraClass={{'is-prefix': true, ...extraClass}}
          disabled={isDisabled}
        />
      )}
      <RowLabelContent
        icon={props.rowIcon}
        label={props.rowLabel}
        onClick={isClickable && !props.rowLabelPrefix && props.onClick}
        hasPrefix={!!props.rowLabelPrefix}
        extraClass={extraClass}
        disabled={isDisabled}
      />
    </>
  );
}

RowLabel.defaultProps = {
  rowLabelTheme: RowLabelTheme.Report,
};

function RowLabelContent(props: {
  icon: SvgIconType;
  label?: string;
  onClick?: () => void;
  hasPrefix?: boolean;
  extraClass?: Record<string, boolean>;
  tooltip?: TooltipAttrs;
  disabled?: boolean;
}) {
  const isClickable = Boolean(props.onClick);
  const slotName = !isClickable ? `selector-icon` : null;
  return (
    <div
      key={`entry-label-${slotName}-${props.icon}`}
      className={clsx(`mp-query-entry-label`, {
        'mp-query-entry-label-session-replay': !!props.extraClass?.[`is-session-replay-filter`],
      })}
      elref='rowLabel'
      slot={slotName}
    >
      {(props.label || props.icon) && (
        <ButtonReact
          elref='rowLabelButton'
          theme={ButtonTheme.Tertiary}
          notFocusable={!props.onClick}
          tooltip={props.tooltip}
          className={clsx(
            {
              'is-clickable': isClickable,
              'has-prefix': props.hasPrefix,
            },
            props.extraClass,
          )}
          onClick={props.onClick}
          {...(props.disabled !== undefined && {disabled: props.disabled})}
        >
          {props.icon ? <SvgIconReact icon={props.icon} /> : <span>{props.label}</span>}
        </ButtonReact>
      )}
    </div>
  );
}

RowLabelContent.defaultProps = {
  label: null,
  hasPrefix: false,
  tooltip: null,
};
